import uniq from 'lodash/uniq'
import {
  type CreateEnterpriseAccountMutationVariables,
  type GetEnterpriseAccountQueryResult,
  type UpdateEnterpriseAccountMutationVariables,
} from 'domains/account-management/api'
import {
  OptionType,
  type RetailerLocationStoreGroup,
  type StoreGroupOption,
} from 'domains/account-management/components/retailer-location-store-group-select/utils/types'
import { type FormData } from './AccountForm.types'

export const toFormData = ({
  canonicalId,
  givenName,
  surname,
  email,
  title,
  phone,
  roles,
  internalAccessConfiguration,
}: GetEnterpriseAccountQueryResult['data']['enterpriseAccount']): FormData => {
  const toOption = (obj: { id: string; name: string }) => ({
    value: obj.id,
    label: obj.name,
  })

  const {
    hasFullPartnerAccess,
    hasFullRetailerAccess,
    hasAllPartnersFullAccess,
    assignedPartners = [],
    assignedRetailerLocations = [],
    assignedRetailers = [],
  } = internalAccessConfiguration

  const roleOptions = roles.map(roleToOption)

  const partnerOptions = assignedPartners.map(toOption)
  const retailerOptions = assignedRetailers.map(toOption)
  const retailerLocationOptions = assignedRetailerLocations.map(toOption)
  // retailerLocationsStoreGroups is store as assignedRetailerLocations
  const retailerLocationsStoreGroups = retailerLocationOptions.map(option => ({
    ...option,
    type: OptionType.RetailerLocation,
  }))

  return {
    canonicalId,
    givenName,
    surname,
    email,
    phone,
    title,
    roles: roleOptions,
    partners: partnerOptions,
    retailers: retailerOptions,
    retailerLocations: retailerLocationOptions,
    retailerLocationsStoreGroups,
    hasAllPartnersFullAccess,
    hasFullRetailerAccess,
    hasFullPartnerAccess,
    createAnother: false,
  }
}

export const roleToOption = (
  role: GetEnterpriseAccountQueryResult['data']['enterpriseAccount']['roles']['0']
) => ({
  value: role.id,
  label: role.name,
  description: role.description,
})

export const fetchId = (obj: { value: string | number }) => obj.value.toString()

const retailerLocationsStoreGroupsToIds = (
  retailerLocationsStoreGroups: RetailerLocationStoreGroup
) => {
  const allIds: string[] = []
  // using forEach here because reduce really does not like the typing of the group options
  retailerLocationsStoreGroups?.forEach(value =>
    value.type === OptionType.StoreGroup
      ? allIds.push(
          ...((value as StoreGroupOption)?.retailerLocations.map(location => location.id) || [])
        )
      : allIds.push(value.value)
  )

  return uniq(allIds)
}

export const toCreatePayload = ({
  givenName,
  surname,
  email,
  title,
  phone,
  hasAllPartnersFullAccess,
  hasFullPartnerAccess,
  hasFullRetailerAccess,
  roles = [],
  partners = [],
  retailers = [],
  retailerLocations = [],
  retailerLocationsStoreGroups = [],
}: FormData): CreateEnterpriseAccountMutationVariables => {
  let retailerLocationIds = []
  if (hasFullRetailerAccess) {
    retailerLocationIds = []
  } else if (retailerLocationsStoreGroups.length) {
    retailerLocationIds = retailerLocationsStoreGroupsToIds(retailerLocationsStoreGroups)
  } else {
    retailerLocationIds = retailerLocations.map(fetchId)
  }

  return {
    givenName,
    surname,
    email,
    title,
    phone,
    hasAllPartnersFullAccess: Boolean(hasAllPartnersFullAccess),
    hasFullPartnerAccess,
    hasFullRetailerAccess,
    roleIds: roles.map(fetchId),
    partnerIds: partners.map(fetchId),
    retailerIds: retailers.map(fetchId),
    retailerLocationIds,
  }
}

export const toUpdatePayload = ({
  canonicalId,
  givenName,
  surname,
  title,
  phone,
  hasAllPartnersFullAccess,
  hasFullPartnerAccess,
  hasFullRetailerAccess,
  roles = [],
  partners = [],
  retailers = [],
  retailerLocations = [],
  retailerLocationsStoreGroups = [],
}: FormData): UpdateEnterpriseAccountMutationVariables => {
  let retailerLocationIds = []
  if (hasFullRetailerAccess) {
    retailerLocationIds = []
  } else if (retailerLocationsStoreGroups.length) {
    retailerLocationIds = retailerLocationsStoreGroupsToIds(retailerLocationsStoreGroups)
  } else {
    retailerLocationIds = retailerLocations.map(fetchId)
  }

  return {
    canonicalId,
    givenName,
    surname,
    title,
    phone,
    hasAllPartnersFullAccess: Boolean(hasAllPartnersFullAccess),
    hasFullPartnerAccess,
    hasFullRetailerAccess,
    roleIds: roles.map(fetchId),
    partnerIds: partners.map(fetchId),
    retailerIds: retailers.map(fetchId),
    retailerLocationIds,
  }
}

export const computeDerivedData = ({
  hasAllPartnersFullAccess,
  hasFullPartnerAccess,
  hasFullRetailerAccess,
  partners,
  retailers,
}) => {
  let canSelectPartners = true
  let canSelectRetailers = partners.length > 0
  let canSelectRetailerLocations = canSelectRetailers && retailers.length > 0

  const partnerIds = partners.map(partner => partner.value)
  const retailerIds = retailers.map(retailer => retailer.value)

  if (hasAllPartnersFullAccess) {
    canSelectPartners = false
    canSelectRetailers = false
    canSelectRetailerLocations = false
    hasFullPartnerAccess = true
    hasFullRetailerAccess = true
  } else if (hasFullPartnerAccess) {
    canSelectPartners = true
    canSelectRetailers = false
    canSelectRetailerLocations = false
    hasFullRetailerAccess = true
  } else if (hasFullRetailerAccess) {
    canSelectPartners = true
    canSelectRetailers = true
    canSelectRetailerLocations = false
  }

  return {
    partnerIds,
    retailerIds,
    canSelectPartners,
    canSelectRetailers,
    canSelectRetailerLocations,
    hasFullPartnerAccess,
    hasFullRetailerAccess,
  }
}
